import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import { EditableProps } from "@builder/components/editable/editableProps";
import { getStyle, getStylePath } from "@builder/components/editable/helpers";
import Input from "@builder/components/general/Input";
import SliderInput from "@builder/components/general/SliderInput";
import UnitInput from "@builder/components/general/UnitInput";
import { getValueUnit } from "@builder/components/general/editUtils";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { DynamicGridNode } from "@common/build/types";

const StyleGrid = ({ parent, title, excluded, stateMode, noWrapper }: EditableProps) => {
    const dispatch = useAppDispatch();
    const gridNode = useAppSelector(getSelectedNode) as DynamicGridNode;
    const cvMode = useAppSelector(state => state.builder.conditionalVariationMode);
    const cvModeIndex = cvMode?.id === gridNode?.id ? cvMode.index : null;

    const gridStyle = getStyle(gridNode, undefined, stateMode, cvModeIndex);
    const gridStylePath = getStylePath(undefined, stateMode, cvModeIndex);

    // Grid Columns / col width
    let rowItemsElem = null;
    let colWidthElem = null;
    if (!excluded || !excluded?.includes("gridTemplateRows")) {
        const gridTC = (gridStyle.stateStyle?.gridTemplateColumns || gridStyle?.default?.gridTemplateColumns) as string;
        let rowItems = "";
        let colWidth = "";

        //check whether there is a fixed number of items per row and allow using auto-fill otherwise
        if (gridTC) {
            const match = gridTC.match(/repeat\((\d+), 1fr\)/);
            if (match) {
                rowItems = match[1];
            } else {
                const matchColWidth = gridTC.match(/repeat\(auto-fill, minmax\(([^,]+), 1fr\)\)/);
                if (matchColWidth) {
                    colWidth = matchColWidth[1];
                }
            }
        }

        const rowItemCallback = (value: string) => {
            if (value === "") {
                dispatch(updateNodeProperty({ propertyPath: [...gridStylePath, "gridTemplateColumns"], value: "" }));
                return;
            }
            const items = parseInt(value);
            if (items) {
                dispatch(updateNodeProperty({ propertyPath: [...gridStylePath, "gridTemplateColumns"], value: `repeat(${items}, 1fr)` }));
            }
        };

        rowItemsElem = <Input label="Items per row" direction="row" placeholder="auto" value={rowItems} callback={rowItemCallback} />;

        if (!rowItems) {
            const colWidthValue = getValueUnit(colWidth);
            const colWidthCallback = (value: string, unit: string) => {
                if (value === "") {
                    dispatch(updateNodeProperty({ propertyPath: [...gridStylePath, "gridTemplateColumns"], value: "" }));
                    return;
                }
                dispatch(
                    updateNodeProperty({
                        propertyPath: [...gridStylePath, "gridTemplateColumns"],
                        value: `repeat(auto-fill, minmax(${value + unit}, 1fr))`
                    })
                );
            };

            colWidthElem = <UnitInput title="Column min width" {...colWidthValue} callback={colWidthCallback} />;
        }
    }

    // Column gap
    let columnGapElem = null;
    if (!excluded || !excluded?.includes("columnGap")) {
        const columnGapString = (gridStyle.stateStyle?.columnGap || gridStyle.default?.columnGap) as string;
        const columnGap = getValueUnit(columnGapString);
        const columnGapCallback = (value: string, unit: string) => {
            dispatch(updateNodeProperty({ propertyPath: [...gridStylePath, "columnGap"], value: value + unit }));
        };
        columnGapElem = <SliderInput title="Space between columns" {...columnGap} min={0} max={50} callback={columnGapCallback} />;
    }

    // Row gap
    let rowGapElem = null;
    if (!excluded || !excluded?.includes("rowGap")) {
        const rowGapString = (gridStyle.stateStyle?.rowGap || gridStyle.default?.rowGap) as string;
        const rowGap = getValueUnit(rowGapString);
        const rowGapCallback = (value: string, unit: string) => {
            dispatch(updateNodeProperty({ propertyPath: [...gridStylePath, "rowGap"], value: value + unit }));
        };
        rowGapElem = <SliderInput title="Space between rows" {...rowGap} min={0} max={50} callback={rowGapCallback} />;
    }

    if (noWrapper) {
        return (
            <>
                {columnGapElem}
                {rowGapElem}
                {/* {colWidthElem} */}
            </>
        );
    }

    return (
        <SidebarCollapse title={title || "Grid"} parent={parent}>
            {rowItemsElem}
            {colWidthElem}
            {columnGapElem}
            {rowGapElem}
        </SidebarCollapse>
    );
};

export default StyleGrid;
